import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { black, lightGrey, mutedGrey, orange, surface2, textMuted, white } from '@/theme/colors'
import { NumberedMappedEvent, UnmappedEvent } from '../types'
import { formatEventTime } from '../utils'
import { fontSizes, text } from '@/theme/typography'
import { motion } from '@/theme/tokens'

interface EventListProps {
	mapped: NumberedMappedEvent[]
	unmapped: UnmappedEvent[]
	selectedEventId: number | null
	onSelect: (eventId: number) => void
}

const Scrollable = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 8px 0;
`

const SectionHeader = styled.div`
	padding: 12px 20px 8px;
	${text.eyebrow}
	color: ${mutedGrey};
`

const Row = styled.button<{ $active: boolean; $muted: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: 100%;
	padding: 12px 20px;
	background: ${props => (props.$active ? `${orange}14` : 'transparent')};
	border: none;
	border-left: 3px solid ${props => (props.$active ? orange : 'transparent')};
	text-align: left;
	cursor: pointer;
	transition: background ${motion.fast};
	opacity: ${props => (props.$muted ? 0.55 : 1)};

	&:hover {
		background: ${props => (props.$active ? `${orange}14` : surface2)};
	}
`

const Badge = styled.div<{ $muted: boolean }>`
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: ${props => (props.$muted ? lightGrey : orange)};
	color: ${white};
	font-weight: 700;
	font-size: ${fontSizes.small};
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 2px;
`

const BadgeDash = styled(Badge)`
	background: ${lightGrey};
	color: ${mutedGrey};
`

const RowBody = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`

const RowTitle = styled.div`
	font-weight: 700;
	font-size: ${fontSizes.base};
	color: ${black};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const RowMeta = styled.div`
	display: flex;
	gap: 8px;
	font-size: ${fontSizes.caption};
	color: ${textMuted};
`

const RowAddress = styled.div`
	font-size: ${fontSizes.caption};
	color: ${textMuted};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const NoLocationLabel = styled.span`
	font-size: ${fontSizes.micro};
	color: ${mutedGrey};
	font-style: italic;
`

const EventList: React.FC<EventListProps> = ({ mapped, unmapped, selectedEventId, onSelect }) => {
	const selectedRef = useRef<HTMLButtonElement | null>(null)

	useEffect(() => {
		if (selectedRef.current) {
			selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		}
	}, [selectedEventId])

	return (
		<Scrollable>
			{mapped.length > 0 && (
				<>
					<SectionHeader>On the map ({mapped.length})</SectionHeader>
					{mapped.map(item => {
						const isActive = selectedEventId === item.event.id
						const time = formatEventTime(item.event.startTime)
						return (
							<Row
								key={item.event.id}
								ref={isActive ? selectedRef : null}
								type="button"
								$active={isActive}
								$muted={false}
								onClick={() => onSelect(item.event.id)}
							>
								<Badge $muted={false}>{item.order}</Badge>
								<RowBody>
									<RowTitle>{item.event.name}</RowTitle>
									<RowMeta>
										<span>Day {item.dayIndex + 1}</span>
										{time && <span>· {time}</span>}
									</RowMeta>
									{item.event.location?.fullText && (
										<RowAddress>{item.event.location.fullText}</RowAddress>
									)}
								</RowBody>
							</Row>
						)
					})}
				</>
			)}
			{unmapped.length > 0 && (
				<>
					<SectionHeader>Without location ({unmapped.length})</SectionHeader>
					{unmapped.map(item => (
						<Row
							key={item.event.id}
							type="button"
							$active={false}
							$muted={true}
							onClick={() => onSelect(item.event.id)}
						>
							<BadgeDash $muted={true}>–</BadgeDash>
							<RowBody>
								<RowTitle>{item.event.name}</RowTitle>
								<RowMeta>
									<span>Day {item.dayIndex + 1}</span>
									<NoLocationLabel>no location</NoLocationLabel>
								</RowMeta>
							</RowBody>
						</Row>
					))}
				</>
			)}
		</Scrollable>
	)
}

export default EventList
